<template>
  <Row>
    <i-col span="24">
      <AffixHeader>
        <BreadcrumbItem slot="parent" to="/events">Events</BreadcrumbItem>
        <span slot="current">{{ action }}</span>
        <span slot="title">{{ action }} Event</span>
        <span slot="sub-title">{{ fullname }}</span>
      </AffixHeader>

      <Tabs v-model="activeTab" :animated="false">
        <!-- Tab: Main -->
        <TabPane label="Main">
          <i-form ref="form" :model="event" label-position="top" :rules="rules">
            <i-form-item>
              <Checkbox v-model="event.data_included">Included in GRID</Checkbox>
            </i-form-item>

            <!-- Name -->
            <i-form-item label="Name" prop="name">
              <i-input v-model="event.name"></i-input>
            </i-form-item>

            <Row>
              <i-col :span="12">
                <i-form-item label="Cause" prop="displacement_type">
                  <i-select v-model="event.displacement_type" style="padding-right:6px;">
                    <i-option value="Conflict">Conflict</i-option>
                    <i-option value="Disaster">Disaster</i-option>
                    <i-option value="Development">Development</i-option>
                  </i-select>
                </i-form-item>
              </i-col>
              <i-col :span="12">
                <i-form-item label="Year" prop="year">
                  <i-select v-model.number="event.year" filterable>
                    <i-option v-for="year in years" :key="year" :value="year">{{ year }}</i-option>
                  </i-select>
                </i-form-item>
              </i-col>
            </Row>

            <Row>
              <!-- Reporting Dates -->
              <i-col span="12">
                <i-form-item label="Start Date" style="padding-right:6px;" prop="start_date.date">
                  <AccuracyDatePicker v-model="event.start_date"></AccuracyDatePicker>
                </i-form-item>
              </i-col>
              <i-col span="12">
                <i-form-item label="End Date" prop="end_date.date">
                  <AccuracyDatePicker v-model="event.end_date"></AccuracyDatePicker>
                </i-form-item>
              </i-col>
            </Row>
            <br />

            <Card dis-hover v-show="isDisaster">
              <p slot="title">
                <FFIcon type="file" />Disaster related Information
              </p>
              <i-form-item label="Hazard Type">
                <Cascader
                  v-model="hazardCascade"
                  :data="HazardTypesTree"
                  filterable
                  @on-change="setHazardTypeID"
                  trigger="hover"
                ></Cascader>
              </i-form-item>
              <GlideNumbers v-model="event.glide_numbers"></GlideNumbers>
            </Card>

            <i-form-item label="Comment">
              <i-input
                v-model="event.comment"
                type="textarea"
                spellcheck
                :autosize="{ minRows: 6, maxRows: 36 }"
                :rows="6"
              ></i-input>
            </i-form-item>

            <!-- Action Buttons -->
            <i-form-item class="action-buttons" v-if="$can('manage', 'events')">
              <i-button type="primary" @click="onCreateEvent" v-if="isNew">
                <FFIcon type="save" />Create
              </i-button>

              <i-button type="primary" @click="onUpdateEvent" v-if="!isNew">
                <FFIcon type="save" />Update
              </i-button>

              <Poptip
                confirm
                title="Are you sure?"
                @on-ok="$router.push(`/events/clone/${event.id}`)"
                v-if="!isNew"
              >
                <i-button :disabled="loading">
                  <FFIcon type="copy" />Clone
                </i-button>
              </Poptip>

              <Poptip confirm title="Are you sure?" @on-ok="onDeleteEvent" v-if="isDeletable">
                <i-button type="error" :disabled="loading">
                  <FFIcon type="remove" />Delete
                </i-button>
              </Poptip>

              <!-- <i-button @click="debug">
                <FFIcon type="terminal"/>Debug
              </i-button>-->
            </i-form-item>
          </i-form>
        </TabPane>

        <TabPane label="Facts" v-if="!isNew">
          <FactsTable :conditions="conditions" :limit="0" :hack="activeTab"></FactsTable>
        </TabPane>
      </Tabs>
    </i-col>

    <LoadingSpinner :loading="loading"></LoadingSpinner>
  </Row>
</template>

<script>
import _ from "lodash";
import Event, {
  getEventByID,
  createEvent,
  updateEvent,
  deleteEventByID,
} from "@/models/Event";
import { HazardTypesTree, getHazardTypeStackByID } from "@/models/HazardType";
import database from "@/database";
import AccuracyDatePicker from "@/components/accuracy_date_picker";
import GlideNumbers from "@/components/glide_numbers";
import FactsTable from "@/facts/table";

export default {
  components: {
    AccuracyDatePicker,
    GlideNumbers,
    FactsTable,
  },
  props: ["id", "action"],
  data() {
    return {
      activeTab: 0,
      loading: false,
      event: {
        id: this.id,
        data_included: true,
        displacement_type: "",
        start_date: {},
        end_date: {},
      },
      hazardCascade: [],
      factCount: 0,
      conditions: {
        event_id: `eq.${this.id || 0}`,
      },
      rules: {
        name: [
          {
            required: true,
            message: "Name is required",
            trigger: "blur",
          },
          {
            min: 12,
            message: "Length should be more than 12",
            trigger: "blur",
          },
        ],
        year: {
          required: true,
          type: "integer",
          message: "Year is required",
        },
        displacement_type: {
          required: true,
          message: "Displacement type is required",
        },
        "start_date.date": {
          required: true,
          message: "Start date is required",
        },
      },
    };
  },
  computed: {
    isNew() {
      return ["New", "Clone"].includes(this.action);
    },
    isDeletable() {
      return !this.isNew && this.factCount === 0;
    },
    isDisaster() {
      return this.event.displacement_type === "Disaster";
    },
    years() {
      const currYear = new Date().getFullYear();
      return _.range(currYear, 1950, -1);
    },
    // TODO: Implement event full name
    fullname() {
      return this.event.name;
    },
    HazardTypesTree() {
      return HazardTypesTree;
    },
  },
  created() {
    switch (this.action) {
      case "Edit":
        this.onLoadEvent(false);
        break;
      case "Clone":
        this.onLoadEvent(true);
        break;
      case "New":
      default:
        this.event = new Event();
        break;
    }
  },
  methods: {
    unpack(event) {
      let hazardCascade = [];
      if (event.hazard_types) {
        const ht = event.hazard_types;
        hazardCascade.push(ht["category"]);
        hazardCascade.push(ht["subcategory"]);
        hazardCascade.push(ht["type"]);
        hazardCascade.push(ht["id"]);
      }
      this.hazardCascade = hazardCascade;

      delete event.hazard_types;

      // Clone Event
      if (this.isClone) {
        delete event.id;
        delete event.serial_no;
        delete event.created_at;
        delete event.created_by;
        delete event.gsn;
      }

      return event;
    },

    async onLoadEvent(isClone) {
      this.loading = true;
      try {
        const event = await getEventByID(this.id, isClone);
        this.event = event;
        this.hazardCascade = getHazardTypeStackByID(event.hazard_type_id);
      } catch (error) {
        console.error(error);
        this.$Message.error("Couldn't load event");
      }
      this.loading = false;
    },

    onCreateEvent() {
      if (!this.$can("manage", "events")) {
        this.$Message.error("User is not permitted");
        return;
      }

      this.$refs["form"].validate(async valid => {
        if (valid) {
          if (!this.validateDates()) return;
          this.loading = true;
          try {
            this.event = await createEvent(_.cloneDeep(this.event));
            this.loadingSuccess("Event has been created");
            this.$router.push(`/events/${this.event.id}`);
          } catch (error) {
            console.error(error);
            this.loadingError("Failed to create event");
          }
          this.loading = false;
        } else {
          this.$Message.error("Form errors!");
        }
      });
    },

    onUpdateEvent() {
      if (!this.$can("manage", "events")) {
        this.$Message.error("User is not permitted");
        return;
      }

      this.$refs["form"].validate(async valid => {
        if (valid) {
          if (!this.validateDates()) return;
          this.loading = true;
          try {
            this.event = await updateEvent(_.cloneDeep(this.event));
            this.loadingSuccess("Event has been updated");
          } catch (error) {
            console.error(error);
            this.loadingError("Failed to update event");
          }
          this.loading = false;
        } else {
          this.$Message.error("Form errors!");
        }
      });
    },

    // Make sure end_date >= start_date
    validateDates() {
      const { start_date, end_date } = this.event;
      if (!!end_date && end_date.date < start_date.date) {
        this.$Notice.error({
          title: "Date error",
          desc: "End date can't be before start date.",
        });
        return false;
      }
      return true;
    },

    async onDeleteEvent() {
      if (!this.$can("delete", "events")) {
        this.$Message.error("User is not permitted");
        return;
      }
      this.loading = true;
      try {
        await deleteEventByID(this.event.id);
        this.loadingSuccess("Successfully deleted");
        this.$router.push(`/events`);
      } catch (error) {
        console.error(error);
        this.loadingError("Failed to delete: " + err.response.data.message);
      }
    },

    // Get the hazard type id from the select
    setHazardTypeID(selection) {
      let hazardCascade = [];
      hazardCascade.push(selection[0]);
      hazardCascade.push(selection[1]);
      hazardCascade.push(selection[2]);
      hazardCascade.push(parseInt(selection[3], 10));
      this.hazardCascade = hazardCascade;
      this.event.hazard_type_id = parseInt(selection[selection.length - 1], 10);
    },
  },
};
</script>

<style lang="css"></style>
